/**
 * Core PWA and Service Worker utilities - Refactored with SOLID principles
 */

// App version is injected by Vite during build
declare global {
  interface Window {
    __APP_VERSION__: string;
    __PWA_ENABLED__: boolean;
  }
}

// Interfaces for better abstraction and testability
export interface INetworkStatusManager {
  isOnline: boolean;
  offlineAt: Date | null;
  onlineAt: Date | null;
  startListening(): void;
  stopListening(): void;
}

export interface IVersionManager {
  getCurrentVersion(): string;
  getStoredVersion(): string | null;
  storeVersion(version: string): void;
  hasVersionChanged(): boolean;
  updateStoredVersion(): void;
}

export interface ICacheManager {
  clearAllCaches(reloadAfter?: boolean, preserveVersion?: boolean): Promise<boolean>;
  getPWAStatus(): Promise<{ cacheDetails: { cachedUrls: string[]; totalCacheSize: number; formattedSize: string } }>;
}

export interface IServiceWorkerManager {
  isEnabled(): boolean;
  register(): Promise<boolean>;
  isActive(): Promise<boolean>;
  unregisterAll(): Promise<boolean>;
}

// ============================================================================
// URL PARAMETER CLEANUP
// Removes ?dpl= parameter added by forceClearCachesAndReload() after the page
// has loaded on the correct deployment. Call EARLY in app initialization.
// ============================================================================

/**
 * Clean up the transient ?dpl= parameter used during deployment transitions.
 * Once loaded, the inline Skew Protection script in index.html sets the correct
 * __vdpl cookie, so ?dpl= is no longer needed.
 *
 * Uses history.replaceState to clean the URL without triggering a reload.
 *
 * @returns true if the parameter was cleaned, false otherwise
 */
export function cleanDeploymentUrlParam(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('dpl')) return false;

    url.searchParams.delete('dpl');
    window.history.replaceState({}, '', url.toString());
    return true;
  } catch (error) {
    console.error('[PWA] Failed to clean URL params:', error);
    return false;
  }
}

/** @deprecated Use cleanDeploymentUrlParam() instead */
export const cleanLegacyUrlParams = cleanDeploymentUrlParam;

/**
 * Network Status Manager - Single Responsibility for network monitoring
 */
class NetworkStatusManager implements INetworkStatusManager {
  private _isOnline: boolean = navigator.onLine;
  private _offlineAt: Date | null = null;
  private _onlineAt: Date | null = null;
  private onlineHandler?: () => void;
  private offlineHandler?: () => void;

  get isOnline(): boolean {
    return this._isOnline;
  }

  get offlineAt(): Date | null {
    return this._offlineAt;
  }

  get onlineAt(): Date | null {
    return this._onlineAt;
  }

  startListening(): void {
    if (typeof window === 'undefined') return;

    this.onlineHandler = () => {
      this._isOnline = true;
      this._onlineAt = new Date();
      document.dispatchEvent(new CustomEvent('app:online'));
    };

    this.offlineHandler = () => {
      this._isOnline = false;
      this._offlineAt = new Date();
      document.dispatchEvent(new CustomEvent('app:offline'));
    };

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
  }

  stopListening(): void {
    if (typeof window === 'undefined') return;

    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
    }
  }
}

// Singleton instance
const networkStatusManager = new NetworkStatusManager();
networkStatusManager.startListening();

// Backward compatibility export
export const networkStatus = {
  get isOnline() { return networkStatusManager.isOnline; },
  get offlineAt() { return networkStatusManager.offlineAt; },
  get onlineAt() { return networkStatusManager.onlineAt; }
};

/**
 * Utility functions
 */
class Utils {
  static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }
}

/**
 * Version Manager - Single Responsibility for version handling
 */
class VersionManager implements IVersionManager {
  private readonly storageKey = 'app_version';

  getCurrentVersion(): string {
    const metaVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
    const windowVersion = window.__APP_VERSION__;
    return metaVersion || windowVersion || 'unknown';
  }

  getStoredVersion(): string | null {
    try {
      return localStorage.getItem(this.storageKey);
    } catch (error) {
      console.error('Failed to get stored app version:', error);
      return null;
    }
  }

  storeVersion(version: string): void {
    try {
      localStorage.setItem(this.storageKey, version);
    } catch (error) {
      console.error('Failed to store app version:', error);
    }
  }

  hasVersionChanged(): boolean {
    const currentVersion = this.getCurrentVersion();
    const storedVersion = this.getStoredVersion();

    if (currentVersion === 'unknown') {
      return false;
    }

    // Only return true if we HAD a stored version and it's DIFFERENT
    // On first visit (no stored version), just store it without reload
    if (!storedVersion) {
      this.storeVersion(currentVersion);
      return false;
    }

    return storedVersion !== currentVersion;
  }

  updateStoredVersion(): void {
    const currentVersion = this.getCurrentVersion();
    if (currentVersion !== 'unknown') {
      this.storeVersion(currentVersion);
    }
  }
}

/**
 * Cache Manager - Single Responsibility for cache operations
 */
class CacheManager implements ICacheManager {
  private readonly versionManager: IVersionManager;

  constructor(versionManager: IVersionManager) {
    this.versionManager = versionManager;
  }

  async getPWAStatus(): Promise<{ cacheDetails: { cachedUrls: string[]; totalCacheSize: number; formattedSize: string } }> {
    const cacheDetails = {
      cachedUrls: [] as string[],
      totalCacheSize: 0,
      formattedSize: '0 B'
    };

    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        const apiCacheNames = cacheNames.filter((name) =>
          name.includes('api-cache') || name.includes('default-cache')
        );

        for (const cacheName of apiCacheNames) {
          const cache = await caches.open(cacheName);
          const requests = await cache.keys();

          for (const request of requests) {
            const url = request.url;
            if (url.includes('/api/')) {
              cacheDetails.cachedUrls.push(url);
            }
          }
        }
      }

      cacheDetails.totalCacheSize = cacheDetails.cachedUrls.length * 1024;
      cacheDetails.formattedSize = Utils.formatBytes(cacheDetails.totalCacheSize);
    } catch (error) {
      console.error('Error getting PWA status:', error);
    }

    return { cacheDetails };
  }

  async clearAllCaches(reloadAfter = false, _preserveVersion = false): Promise<boolean> {
    try {
      let hadServiceWorker = false;
      let swUrl = '';

      // 1. Unregister all service workers
      try {
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          hadServiceWorker = registrations.length > 0;

          if (hadServiceWorker && registrations[0] && registrations[0].active) {
            swUrl = registrations[0].active.scriptURL;
          }

          await Promise.all(registrations.map((reg) => reg.unregister()));
        }
      } catch (error) {
        console.error('Error unregistering service workers:', error);
      }

      // 2. Clear all caches
      try {
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((key) => caches.delete(key)));
        }
      } catch (error) {
        console.error('Error clearing caches:', error);
      }

      // 3. Clear storage
      this.clearStorage();

      // 4. Clear cookies
      this.clearCookies();

      // 5. Clear IndexedDB
      await this.clearIndexedDB();

      // Re-register service worker if needed
      if (hadServiceWorker && swUrl && !reloadAfter) {
        try {
          await navigator.serviceWorker.register(swUrl);
        } catch (error) {
          console.error('Error re-registering service worker:', error);
        }
      }

      if (reloadAfter) {
        setTimeout(() => {
          window.location.href = window.location.origin;
          window.location.reload();
        }, 500);
      }

      return true;
    } catch (error) {
      console.error('Error clearing all browser data:', error);
      return false;
    }
  }

  private clearStorage(): void {
    try {
      // Preserve app_version before clearing - it's stored in localStorage
      // Wiping app_version during cache clear can cause infinite reload loops
      const appVersion = localStorage.getItem('app_version');

      // Clear all localStorage
      localStorage.clear();

      // Restore app_version to prevent version check loops after reload
      if (appVersion) {
        localStorage.setItem('app_version', appVersion);
      }

    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  private clearCookies(): void {
    try {
      // Clear ALL cookies including __vdpl (Vercel Skew Protection)
      // The __vdpl cookie pins the browser to an old deployment for 7 days.
      // When doing a full cache clear, we MUST also clear it so the browser
      // gets routed to the latest deployment. The new index.html will set a
      // fresh __vdpl cookie with the current deployment ID.
      document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0].trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      });
    } catch (error) {
      console.error('Error clearing cookies:', error);
    }
  }

  private async clearIndexedDB(): Promise<void> {
    if (window.indexedDB && indexedDB.databases) {
      try {
        const dbs = await indexedDB.databases();
        await Promise.all(
          dbs.map((db) =>
            db.name && !db.name.includes('firebase') && indexedDB.deleteDatabase(db.name)
          )
        );
      } catch (error) {
        console.error('Error clearing IndexedDB databases:', error);
      }
    }
  }
}

/**
 * Service Worker Manager - Single Responsibility for service worker operations
 */
class ServiceWorkerManager implements IServiceWorkerManager {
  private readonly versionManager: IVersionManager;
  private readonly cacheManager: ICacheManager;

  constructor(versionManager: IVersionManager, cacheManager: ICacheManager) {
    this.versionManager = versionManager;
    this.cacheManager = cacheManager;
  }

  isEnabled(): boolean {
    if (typeof window !== 'undefined' && 'window' in globalThis) {
      return window.__PWA_ENABLED__ !== false;
    }
    return true;
  }

  async isActive(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      return !!registration && !!registration.active;
    } catch {
      return false;
    }
  }

  async register(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    if (!this.isEnabled()) {
      return false;
    }

    try {
      // Check for version change BEFORE registering
      if (this.versionManager.hasVersionChanged()) {
        // CRITICAL: Don't clear caches mid-session - it causes mixed React instances
        // Instead, update the stored version and force a full page reload
        // This ensures the user gets fresh HTML + fresh chunks together
        this.versionManager.updateStoredVersion();
        window.location.reload();
        return false; // Return false since we're reloading
      }

      const registration = await navigator.serviceWorker.register('/sw.js');

      // Listen for service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated') {
              // Don't auto-reload here - let the version manager handle updates
              // This prevents infinite reload loops
              // The version manager will detect version changes and notify the user
            }
          });
        }
      });

      // Check for updates immediately and periodically
      registration.update().catch(() => {});

      // Check for updates every 30 seconds in production
      if (import.meta.env.PROD) {
        setInterval(() => {
          registration.update().catch(() => {});
        }, 30000);
      }

      return true;
    } catch (error) {
      console.error('Error registering service worker:', error);
      return false;
    }
  }

  async unregisterAll(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
      return true;
    } catch (error) {
      console.error('Failed to unregister service workers:', error);
      return false;
    }
  }
}

/**
 * PWA Manager - Facade pattern to coordinate all PWA-related operations
 */
class PWAManager {
  private readonly versionManager: IVersionManager;
  private readonly cacheManager: ICacheManager;
  private readonly serviceWorkerManager: IServiceWorkerManager;
  private readonly networkStatusManager: INetworkStatusManager;

  constructor() {
    this.versionManager = new VersionManager();
    this.cacheManager = new CacheManager(this.versionManager);
    this.serviceWorkerManager = new ServiceWorkerManager(this.versionManager, this.cacheManager);
    this.networkStatusManager = networkStatusManager;
  }

  get version() {
    return this.versionManager;
  }

  get cache() {
    return this.cacheManager;
  }

  get serviceWorker() {
    return this.serviceWorkerManager;
  }

  get network() {
    return this.networkStatusManager;
  }

  isInstalledPWA(): boolean {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSPWA = (window.navigator as any).standalone ||
                     document.referrer.includes('android-app://') ||
                     window.location.hash === '#standalone';
    return isStandalone || isIOSPWA;
  }

}

// Create singleton instance
const pwaManager = new PWAManager();

// Backward compatibility exports
export function isPwaEnabled(): boolean {
  return pwaManager.serviceWorker.isEnabled();
}

export function getCurrentAppVersion(): string {
  return pwaManager.version.getCurrentVersion();
}

export function storeAppVersion(version: string): void {
  return pwaManager.version.storeVersion(version);
}

export function getStoredAppVersion(): string | null {
  return pwaManager.version.getStoredVersion();
}

export function hasAppVersionChanged(): boolean {
  return pwaManager.version.hasVersionChanged();
}

export function updateStoredAppVersion(): void {
  return pwaManager.version.updateStoredVersion();
}

export function isInstalledPWA(): boolean {
  return pwaManager.isInstalledPWA();
}

export async function getPWAStatus() {
  return pwaManager.cache.getPWAStatus();
}

export function registerServiceWorker(): Promise<boolean> {
  return pwaManager.serviceWorker.register();
}

export function isServiceWorkerActive(): Promise<boolean> {
  return pwaManager.serviceWorker.isActive();
}

export function unregisterServiceWorkers(): Promise<boolean> {
  return pwaManager.serviceWorker.unregisterAll();
}

export async function clearAllCaches(reloadAfter = false, preserveVersion = false): Promise<boolean> {
  return pwaManager.cache.clearAllCaches(reloadAfter, preserveVersion);
}

// ============================================================================
// UNIFIED ASSET ERROR HANDLER - SINGLE SOURCE OF TRUTH
// All chunk/CSS/dynamic import errors should go through this function
// ============================================================================

const ASSET_ERROR_RELOAD_KEY = 'unified_asset_reload_attempted';
const ASSET_ERROR_RELOAD_TIMEOUT = 30000; // 30 seconds

/**
 * Check if an error is an asset loading error (chunk, CSS preload, dynamic import, MIME type)
 * These errors typically occur after a deployment when old cached HTML references deleted chunks
 */
export function isAssetLoadingError(error: Error | string): boolean {
  const message = typeof error === 'string' ? error : error?.message || '';
  return (
    message.includes('Loading chunk') ||
    message.includes('Unable to preload CSS') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('ChunkLoadError') ||
    // MIME type error: server returned HTML (404 fallback) instead of JS
    message.includes('MIME type') ||
    message.includes('text/html') ||
    // Module script loading failures (ES modules)
    message.includes('Failed to load module script')
  );
}

/**
 * Detect React version mismatch errors (deployment skew)
 * These occur when old React instance in memory tries to run new chunks
 * Symptoms: hooks return null because different React instance
 */
export function isReactVersionMismatchError(error: Error | string): boolean {
  const message = typeof error === 'string' ? error : error?.message || '';

  // React hooks returning null - sign of multiple React instances
  const reactHookErrors = [
    "Cannot read properties of null (reading 'useState')",
    "Cannot read properties of null (reading 'useRef')",
    "Cannot read properties of null (reading 'useEffect')",
    "Cannot read properties of null (reading 'useContext')",
    "Cannot read properties of null (reading 'useReducer')",
    "Cannot read properties of null (reading 'useCallback')",
    "Cannot read properties of null (reading 'useMemo')",
    "Cannot read properties of null (reading 'useLayoutEffect')",
    // Also catch the generic pattern
    "Cannot read properties of null (reading 'use",
  ];

  return reactHookErrors.some(pattern => message.includes(pattern));
}

/**
 * Check if error is deployment-related (asset loading OR version mismatch)
 */
export function isDeploymentRelatedError(error: Error | string): boolean {
  return isAssetLoadingError(error) || isReactVersionMismatchError(error);
}

/**
 * UNIFIED handler for deployment-related errors (chunks, CSS, dynamic imports, React version mismatch)
 *
 * Handles asset loading errors and React version mismatch errors with:
 * - Infinite reload loop prevention (sessionStorage with 30s timeout)
 * - Full cache clear + deployment-aware reload via clearCachesAndNavigateToLatest()
 *
 * @param error - The error that occurred
 * @param source - Where the error was caught (for logging)
 * @returns true if handled (will reload), false if already attempted recently
 */
export async function handleAssetLoadingError(error: Error | string, source: string = 'unknown'): Promise<boolean> {
  const message = typeof error === 'string' ? error : error?.message || '';

  if (!isDeploymentRelatedError(error)) {
    return false;
  }

  const errorType = isReactVersionMismatchError(error) ? 'React version mismatch' : 'Asset loading';
  const now = Date.now();
  const lastAttempt = sessionStorage.getItem(ASSET_ERROR_RELOAD_KEY);

  // Prevent infinite reload loops
  if (lastAttempt && (now - parseInt(lastAttempt, 10)) < ASSET_ERROR_RELOAD_TIMEOUT) {
    console.warn(`🚫 [${source}] ${errorType} reload already attempted in last 30s - showing error UI instead`);
    console.warn(`   Error: ${message}`);
    return false;
  }

  sessionStorage.setItem(ASSET_ERROR_RELOAD_KEY, now.toString());
  await clearCachesAndNavigateToLatest();
  return true;
}

/**
 * Check if we recently attempted an asset error reload
 */
export function wasRecentAssetReloadAttempted(): boolean {
  const lastAttempt = sessionStorage.getItem(ASSET_ERROR_RELOAD_KEY);
  if (!lastAttempt) return false;
  return (Date.now() - parseInt(lastAttempt, 10)) < ASSET_ERROR_RELOAD_TIMEOUT;
}

/**
 * Clear the asset reload flag (call this on successful app load)
 */
export function clearAssetReloadFlag(): void {
  sessionStorage.removeItem(ASSET_ERROR_RELOAD_KEY);
}

// ============================================================================
// DEPLOYMENT-AWARE RELOAD
// Uses Vercel's ?dpl= query parameter (vercel.com/docs/skew-protection)
// to guarantee routing to the latest deployment.
// ============================================================================

/**
 * Fetch the latest deployment ID from Vercel by requesting without cookies.
 * Without the __vdpl cookie, Vercel routes to the latest deployment.
 * We parse the deployment ID from the inline Skew Protection script in index.html.
 */
async function fetchLatestDeploymentId(): Promise<string | null> {
  try {
    const response = await fetch(window.location.origin + '/', {
      method: 'GET',
      cache: 'reload',       // Bypass disk cache
      credentials: 'omit',   // Don't send __vdpl cookie → routes to LATEST deployment
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
    });
    const html = await response.text();
    const match = html.match(/var\s+deploymentId\s*=\s*'(dpl_[A-Za-z0-9]+)'/);
    if (match?.[1]) {
      return match[1];
    }
    console.warn('   Could not parse deployment ID from latest index.html');
    return null;
  } catch (err) {
    console.warn('   Failed to fetch latest deployment ID (offline?):', err);
    return null;
  }
}

/**
 * Core reload logic shared by forceClearCachesAndReload() and handleAssetLoadingError().
 *
 * 1. Unregister service workers
 * 2. Clear Cache API
 * 3. Fetch latest deployment ID (credentials:'omit' bypasses stale __vdpl cookie)
 * 4. Clear stale __vdpl cookie
 * 5. Navigate with ?dpl=LATEST_ID or fallback to plain reload
 */
async function clearCachesAndNavigateToLatest(): Promise<void> {
  let latestDeploymentId: string | null = null;

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }

    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    latestDeploymentId = await fetchLatestDeploymentId();
  } catch (err) {
    console.error('Failed to clear caches before reload:', err);
  }

  // Clear stale Vercel Skew Protection cookie
  try {
    document.cookie = '__vdpl=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
  } catch { /* cookie clearing is best-effort */ }

  // Navigate with ?dpl= to guarantee correct deployment routing
  if (latestDeploymentId) {
    const url = new URL(window.location.href);
    url.searchParams.set('dpl', latestDeploymentId);
    window.location.href = url.toString();
  } else {
    window.location.reload();
  }
}

/**
 * Force clear all caches and reload to the LATEST deployment.
 * Always reloads unconditionally — use for critical failures or admin-triggered refreshes.
 */
export async function forceClearCachesAndReload(source: string = 'unknown'): Promise<void> {
  await clearCachesAndNavigateToLatest();
}

// ============================================================================
// UNIFIED ERROR LISTENER SETUP - SINGLE INITIALIZATION POINT
// Call this ONCE at app startup. Handles all asset loading errors.
// ============================================================================

let isErrorListenerInitialized = false;

/**
 * Initialize unified asset error listeners - CALL ONCE at app startup
 * This is the SINGLE place where error listeners are set up.
 *
 * Sets up:
 * - Window error listener for script/link loading failures
 * - Unhandled rejection listener for dynamic import failures
 * - Clears reload flags on successful initialization
 */
export function initializeAssetErrorHandling(): void {
  if (typeof window === 'undefined') return;

  // Prevent double initialization
  if (isErrorListenerInitialized) {
    return;
  }

  isErrorListenerInitialized = true;

  // Handler for script/link loading errors (capture phase to catch before bubbling)
  const handleWindowError = (event: ErrorEvent): void => {
    const target = event.target as HTMLElement;

    // Check if it's a script or link element that failed to load
    if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href;

      // Check if it's an asset file (JS or CSS in assets folder)
      if (src && /\/assets\/.*\.(js|css)$/.test(src)) {
        event.preventDefault();
        console.warn('Asset loading failed:', src);
        handleAssetLoadingError(new Error(`Failed to load asset: ${src}`), 'WindowError');
        return;
      }
    }

    // Check error message for deployment-related issues (asset loading or React version mismatch)
    if (event.message && isDeploymentRelatedError(event.message)) {
      event.preventDefault();
      const errorType = isReactVersionMismatchError(event.message) ? 'WindowError-ReactSkew' : 'WindowError-MIME';
      handleAssetLoadingError(new Error(event.message), errorType);
    }
  };

  // Handler for unhandled promise rejections (dynamic imports)
  const handleRejection = (event: PromiseRejectionEvent): void => {
    const error = event.reason;

    if (error && (error.message || typeof error === 'string')) {
      const message = error.message || String(error);

      if (isDeploymentRelatedError(message)) {
        event.preventDefault();
        const errorType = isReactVersionMismatchError(message) ? 'React version mismatch' : 'Asset loading';
        console.warn(`${errorType} error in promise:`, message);
        handleAssetLoadingError(error, 'UnhandledRejection');
      }
    }
  };

  // Set up listeners
  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleRejection);

  // Clear any stale reload flags from previous sessions
  // (The flag should be cleared on successful load, but clear old ones just in case)
  const lastAttempt = sessionStorage.getItem(ASSET_ERROR_RELOAD_KEY);
  if (lastAttempt) {
    const timeSince = Date.now() - parseInt(lastAttempt, 10);
    // If more than 5 minutes old, clear it (stale from previous session)
    if (timeSince > 300000) {
      clearAssetReloadFlag();
    }
  }

  // Expose handler globally for index.html fallback
  (window as any).__handleAssetError = (errorMessage: string) => {
    handleAssetLoadingError(new Error(errorMessage), 'GlobalFallback');
  };

}

/**
 * Check if error handling is initialized
 */
export function isAssetErrorHandlingInitialized(): boolean {
  return isErrorListenerInitialized;
}

// Export the PWA manager for advanced usage
export { pwaManager };
